import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  HeartIcon,
  MapPinIcon,
  StarIcon,
  PhoneIcon,
  MessageCircleIcon,
  FileTextIcon,
  HelpCircleIcon,
} from "lucide-react";
import { useContactAnalytics } from "@/polymet/hooks/use-contact-analytics";
import { useAuth } from "@/lib/auth-context";

interface ProductCardProps {
  id: string;
  slug?: string;
  name: string;
  condition: "New" | "Used" | "Refurbished";
  price: number;
  previousPrice?: number;
  currency?: string;
  image: string;
  year?: number;
  manufacturer: string;
  model: string;
  location: string;
  rating: number;
  reviewCount: number;
  featured?: boolean;
  availableForRent?: boolean;
  categoryId: string;
  subCategoryId: string;
  productCategoryId: string;
  supplierName?: string;
  supplierId?: string;
  className?: string;
  enableDirectQuote?: boolean;
  onDirectQuoteRequest?: () => void;
  isRequestingQuote?: boolean;
}

export default function ProductCard({
  id,
  slug,
  name,
  condition,
  price,
  previousPrice,
  currency = "USD",
  image,
  year,
  manufacturer,
  model,
  location,
  rating,
  reviewCount,
  featured = false,
  availableForRent = false,
  categoryId,
  subCategoryId,
  productCategoryId,
  supplierName,
  supplierId,
  className = "",
  enableDirectQuote = false,
  onDirectQuoteRequest,
  isRequestingQuote = false,
}: ProductCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { suggestIntent, trackContactIntent } = useContactAnalytics();
  
  // State for intent dialog
  const [showContactDialog, setShowContactDialog] = useState(false);
  
  // Track user context for analytics
  const [sessionDuration, setSessionDuration] = useState(0);
  const [hasViewedProduct, setHasViewedProduct] = useState(false);
  const [userType, setUserType] = useState<'new' | 'returning' | 'business'>('new');

  // Track session duration
  useEffect(() => {
    const startTime = Date.now();
    
    // Check if user has viewed this product before
    const viewedProducts = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    setHasViewedProduct(viewedProducts.includes(id));
    
    // Determine user type based on account info and history
    if (user) {
      // Check user account type from profile
      setUserType(user.user_metadata?.user_type || 'returning');
    }

    return () => {
      setSessionDuration(Math.floor((Date.now() - startTime) / 1000));
    };
  }, [id, user]);

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case "New":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "Used":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300";
      case "Refurbished":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const handleCardClick = () => {
    // Track product view
    const viewedProducts = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    if (!viewedProducts.includes(id)) {
      viewedProducts.push(id);
      localStorage.setItem('viewedProducts', JSON.stringify(viewedProducts));
    }

    const productIdentifier = slug || id;
    navigate(`/product/${productIdentifier}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Add favorite logic here
  };

  const handleContactSupplier = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If direct quote is enabled, skip dialog and go straight to quote
    if (enableDirectQuote && onDirectQuoteRequest) {
      onDirectQuoteRequest();
      return;
    }
    
    // Otherwise show intent selection dialog
    setShowContactDialog(true);
  };

  const handleIntentSelection = async (intent: 'quote' | 'inquiry') => {
    setShowContactDialog(false);
    
    // Track analytics
    const userContext = {
      sessionDuration,
      hasViewedProduct,
      userType,
      productPrice: price,
      timeOnPage: sessionDuration
    };

    const suggestedIntent = suggestIntent(userContext);

    await trackContactIntent({
      productId: id,
      userId: user?.id,
      selectedIntent: intent,
      suggestedIntent,
      userContext,
      timestamp: new Date().toISOString()
    });
    
    if (intent === 'quote') {
      // Handle quote request
      const rfqData = {
        productId: id,
        productName: name,
        supplierId: supplierId,
        supplierName: supplierName,
        categoryId: categoryId,
        subcategoryId: subCategoryId,
        productCategoryId: productCategoryId,
        manufacturer: manufacturer,
        model: model,
        year: year,
        condition: condition,
        price: price,
        currency: currency
      };
      
      const queryParams = new URLSearchParams();
      Object.entries(rfqData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      
      navigate(`/create-rfq?${queryParams.toString()}`);
    } else {
      // Handle general inquiry
      if (supplierId && supplierName) {
        navigate(`/suppliers/${supplierName.toLowerCase().replace(/\s+/g, '-')}?tab=contact&product=${id}`);
      } else {
        navigate('/contact');
      }
    }
  };

  return (
    <>
      <Card
        className={`overflow-hidden h-full transition-all hover:shadow-md cursor-pointer ${className}`}
        onClick={handleCardClick}
      >
        <CardHeader className="p-0 relative">
          <div className="block overflow-hidden aspect-[4/3]">
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover transition-transform hover:scale-105"
            />
          </div>

          {featured && (
            <Badge className="absolute top-2 left-2 bg-primary hover:bg-primary">
              Featured
            </Badge>
          )}

          <button
            className="absolute top-2 right-2 p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-colors"
            aria-label="Add to wishlist"
            onClick={handleFavoriteClick}
          >
            <HeartIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>

          <div className="absolute bottom-2 left-2">
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${getConditionColor(condition)}`}
            >
              {condition}
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold text-lg line-clamp-2 leading-tight hover:text-primary transition-colors">
            {name}
          </h3>

          {supplierName && (
            <div className="text-sm font-medium text-gray-700">
              {supplierName}
            </div>
          )}

          <div className="flex items-baseline gap-2">
            <span className="text-sm font-bold text-gray-900">{formatPrice(price)}</span>
            {previousPrice && previousPrice > price && (
              <span className="text-xs text-gray-500 line-through">
                {formatPrice(previousPrice)}
              </span>
            )}
          </div>

          <div className="flex items-center text-sm text-muted-foreground">
            <span className="font-medium">{manufacturer}</span>
            <span className="mx-1">•</span>
            <span>{model}</span>
            {year && (
              <>
                <span className="mx-1">•</span>
                <span>{year}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-1">
            <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400" />
            <span className="font-medium text-sm">{(rating || 0).toFixed(1)}</span>
            <span className="text-xs text-muted-foreground ml-1">
              ({reviewCount || 0} {(reviewCount || 0) === 1 ? "review" : "reviews"})
            </span>
          </div>
        </CardContent>

        <CardFooter className="p-4 pt-0 mt-auto flex flex-col gap-2">
          <Button 
            size="sm" 
            className="w-full" 
            onClick={handleContactSupplier}
            disabled={isRequestingQuote}
          >
            <MessageCircleIcon className="h-4 w-4 mr-1" />
            {isRequestingQuote ? "Sending..." : enableDirectQuote ? "Quick Quote" : "Contact Supplier"}
          </Button>
        </CardFooter>
      </Card>

      {/* Intent Selection Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>How would you like to contact the supplier?</DialogTitle>
            <DialogDescription>
              Choose the option that best describes what you need
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-3">
            <Button
              variant="outline"
              className="justify-start h-auto p-4 text-left"
              onClick={() => handleIntentSelection('quote')}
            >
              <div className="flex items-start gap-3">
                <FileTextIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <div className="font-medium">Get Price Quote</div>
                  <div className="text-sm text-muted-foreground">
                    I'm ready to buy and need pricing & terms
                  </div>
                </div>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="justify-start h-auto p-4 text-left"
              onClick={() => handleIntentSelection('inquiry')}
            >
              <div className="flex items-start gap-3">
                <HelpCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-medium">Ask Questions</div>
                  <div className="text-sm text-muted-foreground">
                    I need more info about this product or company
                  </div>
                </div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
